import { useEffect, useRef, useState } from 'react';
import { FaGlobe, FaSpinner } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { socialService } from '../../services/apiService';
import LinkifiedText from '../LinkifiedText';
import '../PostTranslateControl/PostTranslateControl.css';

function shouldOfferTranslation(item, siteLanguage) {
  const title = String(item?.title || '').trim();
  const description = String(item?.description || '').trim();
  if (!title && !description) return false;
  const itemLang = item?.language || 'und';
  if (itemLang === siteLanguage) return false;
  return true;
}

function descriptionOverflows(el) {
  if (!el || el.clientWidth < 8) return false;
  return el.scrollHeight > el.clientHeight + 1;
}

function hasWorkPeriod(item) {
  return Boolean(item?.currentlyWorking || item?.startYear || item?.endYear);
}

function formatWorkPeriodRange(item, t) {
  const start = item?.startYear ? Number(item.startYear) : null;
  const end = item?.endYear ? Number(item.endYear) : null;
  const active = Boolean(item?.currentlyWorking);

  if (start && active) {
    return t('hub.workPeriod.rangePresent', { start });
  }
  if (start && end) {
    if (start === end) return String(start);
    return t('hub.workPeriod.rangeClosed', { start, end });
  }
  if (start) return String(start);
  if (end) return String(end);
  return '';
}

function WorkPeriodMeta({ item }) {
  const { t } = useTranslation();
  if (!hasWorkPeriod(item)) return null;

  const range = formatWorkPeriodRange(item, t);
  const active = Boolean(item.currentlyWorking);

  return (
    <div className="hub-work-period" aria-label={t('hub.workPeriod.label')}>
      {range ? (
        <span className="hub-work-period-range">{range}</span>
      ) : null}
      <span className={`hub-work-period-status${active ? ' is-active' : ' is-past'}`}>
        {active ? t('hub.workPeriod.active') : t('hub.workPeriod.past')}
      </span>
    </div>
  );
}

export default function HubItemContentWithTranslate({ item }) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [mode, setMode] = useState('original');
  const [translated, setTranslated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const descriptionRef = useRef(null);
  const expandedRef = useRef(false);

  const offer = item ? shouldOfferTranslation(item, language) : false;
  const displayTitle = mode === 'translated' && translated?.title
    ? translated.title
    : (item?.title || '');
  const displayDescription = mode === 'translated' && translated
    ? (translated.description || '')
    : (item?.description || '');
  const hasDescription = Boolean(String(displayDescription).trim());

  expandedRef.current = expanded;

  useEffect(() => {
    setMode('original');
    setTranslated(null);
    setError('');
    setExpanded(false);
    setNeedsExpand(false);
  }, [item?.id, item?.title, item?.description, language]);

  useEffect(() => {
    if (!hasDescription) {
      setNeedsExpand(false);
      return undefined;
    }

    let cancelled = false;
    let rafIds = [];

    const measure = () => {
      if (cancelled || expandedRef.current) return;
      const el = descriptionRef.current;
      if (!el) return;
      setNeedsExpand(descriptionOverflows(el));
    };

    const scheduleMeasure = () => {
      measure();
      rafIds.push(requestAnimationFrame(() => {
        measure();
        rafIds.push(requestAnimationFrame(measure));
      }));
    };

    scheduleMeasure();

    const el = descriptionRef.current;
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(scheduleMeasure)
      : null;

    if (resizeObserver && el) {
      resizeObserver.observe(el);
      if (el.parentElement) resizeObserver.observe(el.parentElement);
    }

    const onResize = () => scheduleMeasure();
    window.addEventListener('resize', onResize);

    const fontsPromise = document.fonts?.ready;
    if (fontsPromise?.then) {
      fontsPromise.then(() => {
        if (!cancelled) scheduleMeasure();
      });
    }

    // Carrossel anima o slide; remede após a transição estabilizar
    const delayed = window.setTimeout(scheduleMeasure, 320);

    return () => {
      cancelled = true;
      rafIds.forEach((id) => cancelAnimationFrame(id));
      window.clearTimeout(delayed);
      window.removeEventListener('resize', onResize);
      resizeObserver?.disconnect();
    };
  }, [displayDescription, hasDescription, mode, item?.id]);

  if (!item) return null;

  const handleTranslate = async () => {
    if (mode === 'translated') {
      setMode('original');
      return;
    }

    const leakedTranslation = Boolean(
      translated
      && (
        /VIHS[_-\s]*MENTION|«\s*VH[MU]/i.test(translated.title || '')
        || /VIHS[_-\s]*MENTION|«\s*VH[MU]/i.test(translated.description || '')
      )
    );

    if (translated && !leakedTranslation) {
      setMode('translated');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await socialService.translateHubItem(item.id, language);
      const next = res?.data?.translation;
      if (!next?.title && !next?.description) {
        throw new Error(t('translate.failed'));
      }
      setTranslated({
        title: next.title || item.title,
        description: next.description || '',
      });
      setMode('translated');
      setExpanded(false);
    } catch (err) {
      setError(err.message || t('translate.failed'));
    } finally {
      setLoading(false);
    }
  };

  const actionLabel = (() => {
    if (loading) return t('translate.translating');
    if (mode === 'translated') return t('translate.showOriginal');
    if (translated) return t('translate.showTranslation');
    return language === 'en' ? t('translate.toEnglish') : t('translate.toPortuguese');
  })();

  return (
    <div className="hub-feature-copy-main post-translate-block">
      <h4>{displayTitle}</h4>
      <WorkPeriodMeta item={item} />
      {hasDescription && (
        <div className={`hub-feature-description-block${expanded ? ' is-expanded' : ''}`}>
          <LinkifiedText
            ref={descriptionRef}
            text={displayDescription}
            className="hub-feature-description"
          />
          {(needsExpand || expanded) && (
            <button
              type="button"
              className="hub-feature-see-more"
              onClick={() => setExpanded((current) => !current)}
            >
              {expanded ? t('common.seeLess') : t('common.seeMore')}
            </button>
          )}
        </div>
      )}
      {offer && (
        <div className="post-translate-actions">
          <button
            type="button"
            className={`post-translate-btn${mode === 'translated' ? ' is-active' : ''}`}
            onClick={handleTranslate}
            disabled={loading}
          >
            {loading ? <FaSpinner className="social-spin" aria-hidden="true" /> : <FaGlobe aria-hidden="true" />}
            <span>{actionLabel}</span>
          </button>
          {error && <span className="post-translate-error">{error}</span>}
        </div>
      )}
    </div>
  );
}
